import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider } from './auth/AuthProvider';
import ProtectedRoute from './auth/ProtectedRoute';
import Header from './components/Layout/Header';
import Sidebar from './components/Layout/Sidebar';
import useStore from './store/useStore';
import Dashboard from './pages/Dashboard';
import MyAssets from './pages/MyAssets';
import AssetDetail from './pages/AssetDetail';
import Events from './pages/Events';
import Threats from './pages/Threats';
import Playbooks from './pages/Playbooks';
import Advisor from './pages/Advisor';
import OrgMembers from './pages/OrgMembers';
import AuditLogs from './pages/AuditLogs';
import NetworkMap from './pages/NetworkMap';
import SbomRecords from './pages/SbomRecords';
import BaselineManagement from './pages/BaselineManagement';
import ActiveAlerts from './pages/ActiveAlerts';
import MyCERTInsights from './pages/MyCERTInsights';
import FeedMonitoring from './pages/FeedMonitoring';
import IndicatorVault from './pages/IndicatorVault';
import RiskAssessment from './pages/RiskAssessment';
import Integrations from './pages/Integrations';
import PlatformOrganizations from './pages/PlatformOrganizations';
import Scanner from './pages/Scanner';
import Vulnerabilities from './pages/Vulnerabilities';
import AgentRegistry from './pages/AgentRegistry';
import Login from './auth/Login';
import AuthCallback from './auth/AuthCallback';
import InvitationAccept from './pages/InvitationAccept';

const AppLayout = ({ children }) => {
  const { sidebarMinimized } = useStore();
  return (
    <div className="flex h-screen bg-gray-950">
      <Sidebar />
      <div className={`flex-1 flex flex-col ${sidebarMinimized ? 'ml-16' : 'ml-60'} transition-all duration-300 ease-in-out`}>
        <Header />
        <main className="flex-1 overflow-y-auto bg-gray-950">
          {children}
        </main>
      </div>
    </div>
  );
};

const AppRoutes = () => {
  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route path="/auth/callback" element={<AuthCallback />} />
      <Route path="/invitation/accept" element={<InvitationAccept />} />
      <Route path="/" element={
        <ProtectedRoute>
          <AppLayout>
            <Dashboard />
          </AppLayout>
        </ProtectedRoute>
      } />
      <Route path="/assets" element={
        <ProtectedRoute requiredPermissions={['assets:read']}>
          <AppLayout>
            <MyAssets />
          </AppLayout>
        </ProtectedRoute>
      } />
      <Route path="/assets/:id" element={
        <ProtectedRoute requiredPermissions={['assets:read']}>
          <AppLayout>
            <AssetDetail />
          </AppLayout>
        </ProtectedRoute>
      } />
      <Route path="/events" element={
        <ProtectedRoute requiredPermissions={['events:read']}>
          <AppLayout>
            <Events />
          </AppLayout>
        </ProtectedRoute>
      } />
      <Route path="/threats" element={
        <ProtectedRoute requiredPermissions={['events:read']}>
          <AppLayout>
            <Threats />
          </AppLayout>
        </ProtectedRoute>
      } />
      <Route path="/playbooks" element={
        <ProtectedRoute requiredPermissions={['playbooks:view']}>
          <AppLayout>
            <Playbooks />
          </AppLayout>
        </ProtectedRoute>
      } />
      <Route path="/advisor" element={
        <ProtectedRoute requiredPermissions={['playbooks:view']}>
          <AppLayout>
            <Advisor />
          </AppLayout>
        </ProtectedRoute>
      } />
      <Route path="/org/members" element={
        <ProtectedRoute requiredPermissions={['users:read']}>
          <AppLayout>
            <OrgMembers />
          </AppLayout>
        </ProtectedRoute>
      } />
      <Route path="/audit-logs" element={
        <ProtectedRoute requiredPermissions={['logs:read']}>
          <AppLayout>
            <AuditLogs />
          </AppLayout>
        </ProtectedRoute>
      } />
      <Route path="/network-map" element={
        <ProtectedRoute requiredPermissions={['assets:read']}>
          <AppLayout>
            <NetworkMap />
          </AppLayout>
        </ProtectedRoute>
      } />
      <Route path="/scanner" element={
        <ProtectedRoute requiredPermissions={['assets:read']}>
          <AppLayout>
            <Scanner />
          </AppLayout>
        </ProtectedRoute>
      } />
      <Route path="/vulnerabilities" element={
        <ProtectedRoute requiredPermissions={['assets:read']}>
          <AppLayout>
            <Vulnerabilities />
          </AppLayout>
        </ProtectedRoute>
      } />
      <Route path="/agents" element={
        <ProtectedRoute requiredPermissions={['assets:read']}>
          <AppLayout>
            <AgentRegistry />
          </AppLayout>
        </ProtectedRoute>
      } />
      <Route path="/sbom" element={
        <ProtectedRoute requiredPermissions={['sbom:read']}>
          <AppLayout>
            <SbomRecords />
          </AppLayout>
        </ProtectedRoute>
      } />
      <Route path="/baseline" element={
        <ProtectedRoute requiredPermissions={['assets:read']}>
          <AppLayout>
            <BaselineManagement />
          </AppLayout>
        </ProtectedRoute>
      } />
      <Route path="/drift-alerts" element={
        <ProtectedRoute requiredPermissions={['assets:read']}>
          <AppLayout>
            <ActiveAlerts />
          </AppLayout>
        </ProtectedRoute>
      } />
      <Route path="/mycert" element={
        <ProtectedRoute requiredPermissions={['events:read']}>
          <AppLayout>
            <MyCERTInsights />
          </AppLayout>
        </ProtectedRoute>
      } />
      <Route path="/feed-monitoring" element={
        <ProtectedRoute requiredPermissions={['events:read']}>
          <AppLayout>
            <FeedMonitoring />
          </AppLayout>
        </ProtectedRoute>
      } />
      <Route path="/indicator-vault" element={
        <ProtectedRoute requiredPermissions={['events:read']}>
          <AppLayout>
            <IndicatorVault />
          </AppLayout>
        </ProtectedRoute>
      } />
      <Route path="/risk-assessment" element={
        <ProtectedRoute requiredPermissions={['events:read']}>
          <AppLayout>
            <RiskAssessment />
          </AppLayout>
        </ProtectedRoute>
      } />
      <Route path="/integrations" element={
        <ProtectedRoute requiredPermissions={['system:update']}>
          <AppLayout>
            <Integrations />
          </AppLayout>
        </ProtectedRoute>
      } />
      <Route path="/platform/organizations" element={
        <ProtectedRoute requiredRoles={['Superadmin']}>
          <AppLayout>
            <PlatformOrganizations />
          </AppLayout>
        </ProtectedRoute>
      } />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
};

function App() {
  return (
    <Router>
      <AuthProvider>
        <AppRoutes />
      </AuthProvider>
    </Router>
  );
}

export default App;
